import React, {useMemo} from "react";
import {Link, useHistory, useLocation} from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import {useTranslation} from "react-i18next";
import FeatherIcon from "feather-icons-react";
import {getInitials, Block} from "../../constants";
import TournamentMenu from "./TournamentMenu";

// baseColor/highlightColor left to the app-wide SkeletonTheme default (see
// index.js) - these all used to hardcode baseColor===highlightColor==='#fff'
// (or, for the tabs-loading placeholder below, a near-white "#fff"/"#f3f3f3"
// pair), which had no shimmer contrast on light theme and read as a plain
// white block sitting on the dark theme's own dark header background.
// heroProgress (default 1, matching the real header's own "no hero at all"
// fallback) - without this, this identity row (back-arrow+avatar+title
// skeleton) rendered at permanent full opacity directly below
// TournamentHero.js's own identical-shaped loading row (it has its own
// circle avatar + skeleton table, right above this), reading as two stacked
// headers while a tournament is loading. The real (post-load) TournamentHeader
// fades this same row in only once scrolled past the hero (opacity:
// heroProgress, see its own comment) - applying that here too makes the
// loading state agree with it: at scrollY 0 only the hero's own skeleton
// shows, this one fades in on scroll same as the real identity row would.
// The tabs-row skeleton below is NOT faded, matching the real header's tabs
// row (always visible regardless of hero scroll) - loading or not, that's
// reserved layout space nothing else is covering.
export const TournamentHeaderSkeleton = ({ heroProgress = 1 }) => (
    <div className="tournament-header-wrap">
        <div className="tournament-header-wrap-inner">
            <div className="tournament-header-top" style={{ opacity: heroProgress }}>
                <Skeleton width={20} height={20} />
                <Skeleton circle={true} width={50} height={50} />
                <div className="tournament-header-titles" style={{ paddingTop: '3px' }}>
                    <Skeleton width={160} height={16} style={{ marginBottom: '4px' }} />
                    <Skeleton width={80} height={11} />
                </div>
            </div>
            <div className="tournament-header-tabs-wrap">
                <ul className="tournament-header-tabs" style={{ margin: '15px 0 0 0' }}>
                    <li><Skeleton width={90} height={20} /></li>
                    <li><Skeleton width={70} height={20} /></li>
                    <li><Skeleton width={80} height={20} /></li>
                    <li><Skeleton width={85} height={20} /></li>
                </ul>
            </div>
        </div>
    </div>
);

export const TABS = [
    {key: "information", label: "Information"},
    {key: "players", label: "Participants", blockType: Block.PARTICIPANTS},
    {key: "draw-stages", label: "Draw", blockTypes: [Block.GROUPS, Block.DRAWS]},
    {key: "schedule", label: "Schedule", blockType: Block.SCHEDULE},
    // Back as a real tab (own ?tab=rating page, rendering the same
    // RatingTable.js the admin's standalone /admin/ratings/:id page and the
    // rating-badge modal both already use - not TournamentRatingPlayers,
    // which is this tournament's own results list, a different thing) -
    // instead of only reachable through that modal bus (Header.js's
    // openTournamentRating, still used as-is for quick lookups from other
    // tabs - no full tab remount for a badge click). Labeled "Results", not
    // "Rating", and no longer gated behind a linked rating base - its own
    // "Tournament points" sub-tab (TournamentRatingTab.js) is this
    // tournament's own place/score results, which stand on their own
    // without one (see TournamentRating.js's own comment); only the
    // "Overall rating" sub-tab actually needs tournament.rating, and
    // degrades on its own (RatingTable with no ratingId) when there isn't
    // one.
    {key: "rating", label: "Results", blockType: Block.RANKING},
];

// Maps each real nav tab above to the DOM id its content renders under -
// the same ids TournamentExportModal.js's hidden iframe already
// screenshots one at a time via export=true. Shared by Tournament.js
// (mounts the actual #<id> element) and Header.js (the bottom-nav
// Screenshot button, which lives in a separate React tree and only knows
// the active tab via the ?tab= query param, not Tournament.js's state).
export const SCREENSHOT_TARGETS = {
    information: { targetId: "information-block", fileName: "Information" },
    players: { targetId: "participants-block", fileName: "Participants" },
    "draw-stages": { targetId: "draw-stages-block", fileName: "Draw" },
    schedule: { targetId: "schedule-block", fileName: "Schedule" },
    rating: { targetId: "rating-block", fileName: "Rating" },
};

// shared with TournamentSwipeTabs, so swiping lands on the same next/prev
// tab the header's own nav would show - a block disabled for public view
// shouldn't be swipeable into even though it's still in TABS.
export function getVisibleTabs(tournament, isAuthorized) {
    return TABS.filter(tab => {
        // Checked before the isAuthorized shortcut below - a tab.requires
        // failure (e.g. "rating" with no rating base linked) means there's
        // nothing that tab could actually render, for admin and public
        // viewers alike, not just a permission/visibility toggle.
        if (tab.requires && !tab.requires(tournament)) return false;
        if (isAuthorized) return true;
        const blockTypes = tab.blockTypes || (tab.blockType ? [tab.blockType] : []);
        if (blockTypes.length === 0) return true;
        // Merged "draw-stages" tab covers two blocks (Groups/Draws) - visible
        // as long as at least one of them is active, not only when both are.
        return blockTypes.some(blockType => {
            const block = tournament?.blocks?.find(b => b.block_type === blockType);
            return block ? block.is_active : true;
        });
    });
}

export default function TournamentHeader({
    tournament, tournamentId, isAuthorized, wrapRef, heroProgress, goUp, onToggleSearch,
    user, theme, setTheme, accent, setAccent, compactTable, setCompactTable,
    language, handleLanguageChange
}) {
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();

    const openClubModal = (e) => {
        e.preventDefault();
        const params = new URLSearchParams(location.search);
        params.set("club_id", tournament.club);
        // replace, not push - same reasoning as the tab-switch fix above
        // (see arrivalActionRef's own comment): a push here left an extra
        // history entry goUp's own goBack() would undo first, so pressing
        // the back arrow after opening the club card just closed the card
        // instead of actually leaving the tournament.
        history.replace({ search: params.toString() });
    };

    const activeTab = new URLSearchParams(location.search).get("tab") || "information";

    const visibleTabs = useMemo(() => getVisibleTabs(tournament, isAuthorized), [tournament, isAuthorized]);

    // Carries the current stage/place-tab selection along into the target
    // tab's own URL instead of the plain `?tab=X` this used to build, which
    // reset every other param - so leaving Draw on stage 2 (or Grids on a
    // particular place tab) for Players/Schedule/etc. and coming back via
    // this nav lost the selection, always landing back on stage 1. Only
    // `stage`/`gridTab` are carried, not the whole querystring (other
    // params like player_id/club_id open unrelated overlays that shouldn't
    // follow a tab switch) - safe to hand to a tab that doesn't use them
    // (e.g. Schedule inheriting Draw's `stage`) since each tab already
    // validates it against its own stage list before trusting it, falling
    // back to its own default otherwise.
    const buildTabHref = (tabKey) => {
        const params = new URLSearchParams();
        params.set("tab", tabKey);
        if (isAuthorized) params.set("admin", "1");
        const currentParams = new URLSearchParams(location.search);
        const stage = currentParams.get("stage");
        if (stage) params.set("stage", stage);
        const gridTab = currentParams.get("gridTab");
        if (gridTab) params.set("gridTab", gridTab);
        return `/tournaments/${tournamentId}?${params.toString()}`;
    };

    if (!tournament && tournamentId !== "new") {
        return <TournamentHeaderSkeleton heroProgress={heroProgress} />;
    }

    const clubImage = tournament?.club_logo;
    const clubName = tournament?.club_name;

    return (
        <div ref={wrapRef} className="tournament-header-wrap">
            <div className="tournament-header-wrap-inner">
                <div className="tournament-header-top" style={{ opacity: heroProgress }}>
                    {/* Crossfades in as a whole row as Tournament.js's own hero
                        cover scrolls away (see heroProgress prop) - stays
                        permanently at full opacity whenever there's no hero to
                        begin with, since heroProgress defaults to (and gets reset
                        to) 1 for that case. The back arrow and "..." menu here
                        are their own copy of TournamentHero.js's own top row (not
                        a shared instance) - safe to duplicate now that both are
                        opacity-linked to the same heroProgress value: the hero's
                        copy is already scrolled out of the viewport by the time
                        this row reaches full opacity, so there's never a moment
                        with two fully-opaque copies on screen at once (the
                        now-stale "that read as duplicated controls" problem this
                        comment used to describe was from an earlier version
                        where only the crest+title faded and the icons sat next
                        to it at permanent full opacity instead). */}
                    <a href="#" onClick={goUp} className="tournament-header-back" style={{border: 0, color: 'var(--primary-color)'}}>
                        <FeatherIcon icon="arrow-left" size={20} />
                    </a>
                    <div className="tournament-header-identity">
                        <div className="tournament-header-thumb">
                            {clubImage
                                ? <img
                                    src={clubImage.startsWith('http') || clubImage.startsWith('/') ? clubImage : `/media/${clubImage}`}
                                    className="thumbnail initials-home"
                                    alt={clubName}
                                    style={{ margin: 0 }}
                                />
                                : clubName &&
                                <div className="no-thumbnail initials-home" style={{ margin: 0 }}>
                                    {getInitials(clubName)}
                                </div>
                            }
                        </div>

                        {/* same title/club styling (and truncation) as the tournament
                            list card's .tournament-card-title/-club, name-first. flex +
                            min-width:0 (not a table cell width hack) is what actually
                            makes the ellipsis work when the name/club is long. */}
                        <div className="tournament-header-titles">
                            <div className="tournament-card-title">
                                {(tournament?.name || t("New tournament"))}
                            </div>
                            <div className="tournament-card-club">
                                {clubName && tournament?.club ? (
                                    <a href="#" onClick={openClubModal} className="tournament-header-club-link" title={t("Club page")}>
                                        <span className="tournament-header-club-name">{clubName}</span>
                                        <FeatherIcon icon="external-link" size={11} />
                                    </a>
                                ) : (clubName || '')}
                            </div>
                        </div>
                    </div>
                    <div className="tournament-header-right">
                        {/* Mini/sticky header keeps only Search as its own
                            icon - everything else (Share/Export included)
                            stays collapsed into the "..." menu below, since
                            this row is already the cramped one. The hero's
                            top row (TournamentHero.js, shown first before any
                            scroll) is where Share/Export/Screenshot get their
                            own standalone icons instead. */}
                        {onToggleSearch && (
                            <a
                                href="#"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => { e.preventDefault(); onToggleSearch(); }}
                                className="tournament-header-search-toggle"
                                style={{ border: 0, color: 'var(--primary-color)' }}
                                title={t("Search")}
                            >
                                <FeatherIcon icon="search" size={16} />
                            </a>
                        )}
                        <div className="menu-container">
                            <ul className="absolute_menu mode">
                                <li className="second">
                                    <TournamentMenu
                                        id={tournamentId}
                                        user={user}
                                        tournament={tournament}
                                        screenshotTarget={SCREENSHOT_TARGETS[activeTab]}
                                        isAuthorized={isAuthorized}
                                        theme={theme}
                                        setTheme={setTheme}
                                        accent={accent}
                                        setAccent={setAccent}
                                        compactTable={compactTable}
                                        setCompactTable={setCompactTable}
                                        language={language}
                                        handleLanguageChange={handleLanguageChange}
                                    />
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="tournament-header-tabs-wrap">
                    <ul className="tournament-header-tabs">
                        {!tournament ? (
                            <li><Skeleton width={420} height={20} /></li>
                        ) : (
                            visibleTabs.map((tab) => (
                                <li key={tab.key} className={activeTab === tab.key ? "active" : ""}>
                                    <Link replace to={buildTabHref(tab.key)}>
                                        {t(tab.label)}
                                    </Link>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}
